import React, { useState } from 'react';
import { Row, Col, Card, Upload, Select, Button, message, Spin } from 'antd';
import { InboxOutlined, ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ExcelJS from 'exceljs';
import { heuristicMatch } from '../../../../services/reservation';
import evaluationTypes from '../../../../utils/evaluationType.json';
import {
  calculateNumericDensity,
  calculateCardinality,
  evaluateDateType,
  resolveStaticVocabularies,
  documentNumberRegex,
  emailRegex,
  phoneRegex,
  thresholds,
  resolveFuzzyHeaders,
} from './massiveChargeHeuristics';
const { Dragger } = Upload;
const { Option } = Select;
const MassiveChargeStep2 = ({ form, setCurrentStep, fieldsToInclude, onMappingComplete }) => {
  const { t } = useTranslation();
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [loading, setLoading] = useState(false);
  const [fileData, setFileData] = useState(null);
  const handleBeforeUpload = (file) => {
    const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.name.endsWith('.xlsx');
    if (!isExcel) {
      message.error(t('reservations.massiveCharge.step2ErrorNotExcel'));
      return Upload.LIST_IGNORE;
    }
    setLoading(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const buffer = e.target.result;
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(buffer);
        const worksheet = workbook.worksheets[0];
        const firstRow = worksheet.getRow(1);
        const columnNames = [];
        const getColumnLetter = (colNumber) => {
          let letter = '';
          let temp = colNumber;
          while (temp > 0) {
            let remainder = (temp - 1) % 26;
            letter = String.fromCharCode(65 + remainder) + letter;
            temp = Math.floor((temp - 1) / 26);
          }
          return letter;
        };
        firstRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
          if (cell.value) {
             const baseName = cell.value.toString().trim();
             let proposedName = baseName;
             if (columnNames.includes(proposedName)) {
                proposedName = `${baseName} (Col ${getColumnLetter(colNumber)})`;
             }
             columnNames.push(proposedName);
          }
        });
        if (columnNames.length === 0) {
          message.error(t('reservations.massiveCharge.step2ErrorEmptyFile'));
        } else {
          const rows = [];
          worksheet.eachRow((row, rowNumber) => {
            if (rowNumber > 1) { 
              const rowData = {};
              row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
                const header = columnNames[colNumber - 1];
                if (header) {
                  let val = cell.value;
                  if (val && typeof val === 'object' && val.result !== undefined) val = val.result;
                  if (val && val instanceof Date) val = val.toISOString().split('T')[0];
                  rowData[header] = val;
                }
              });
              rows.push(rowData);
            }
          });
          if (rows.length > 250) {
            message.error(t('reservations.massiveCharge.step2MaxRecordsError'));
            setHeaders([]);
            setFileData(null);
            setMapping({});
            return;
          }
          setHeaders(columnNames);
          setFileData(rows);
          const newMapping = {};
          const candidateBins = {};
          const unmappedColumns = [];
          const addCandidate = (key, col, source) => {
             if (!candidateBins[key]) candidateBins[key] = [];
             if (!candidateBins[key].includes(col)) {
                candidateBins[key].push(col);
             }
          };
          fieldsToInclude.forEach(field => {
            const label = t(`reservations.massiveCharge.fields.${field.key}`).toLowerCase();
            if (field.key === 'protocol') {
              const matches = columnNames.filter(h => h.toLowerCase() === label || h.toLowerCase().includes(label));
              matches.forEach(m => addCandidate(field.key, m, 'Fase 0 (Extricta)'));
            } else {
              const matches = columnNames.filter(h => h.toLowerCase() === label || h.toLowerCase().includes(label));
              matches.forEach(m => addCandidate(field.key, m, 'Fase 0 (Extricta)'));
            }
          });
          Object.values(candidateBins).flat().forEach(mappedCol => {
            if (!unmappedColumns.includes(mappedCol)) unmappedColumns.push(mappedCol);
          });
          let orphanColumns = columnNames.filter(col => !unmappedColumns.includes(col));
          const missingFields = fieldsToInclude.filter(f => !candidateBins[f.key] || candidateBins[f.key].length === 0);
          if (missingFields.length > 0 && orphanColumns.length > 0) {
            const fuzzyAssignments = resolveFuzzyHeaders(orphanColumns, missingFields, t);
            Object.entries(fuzzyAssignments).forEach(([fieldKey, colOrCols]) => {
              const cols = Array.isArray(colOrCols) ? colOrCols : [colOrCols];
              cols.forEach(c => addCandidate(fieldKey, c, 'Fase 1.5 (Fuzzy Header)'));
            });
            const newlyMapped = Object.values(fuzzyAssignments).flat();
            orphanColumns = orphanColumns.filter(col => !newlyMapped.includes(col));
          }
          const getCleanValue = (val) => {
             if (val === null || val === undefined) return '';
             if (val instanceof Date) return val.toISOString();
             if (typeof val === 'object') return val.result || val.text || '';
             return String(val);
          };
          let potentialIdentities = [];
          if (orphanColumns.length > 0) {
            const companyId = form?.getFieldValue('company_id');
            const samplesPayload = {};
            const localAssignments = {};
            orphanColumns.forEach(col => {
              const allValues = rows.map(r => getCleanValue(r[col])).filter(v => v !== '');
              const samples = [...new Set(allValues)].slice(0, 3);
              if (samples.length === 0) return;
              const firstSample = String(samples[0]).trim();
              if (documentNumberRegex.test(firstSample) && calculateNumericDensity(firstSample) > thresholds.documentNumberDensity) {
                localAssignments[col] = 'documentNumber';
                return;
              }
              const hasEmails = samples.every(s => emailRegex.test(String(s).trim()));
              if (hasEmails) {
                 localAssignments[col] = 'email';
                 return;
              }
              const hasPhones = samples.every(s => phoneRegex.test(String(s).trim()));
              if (hasPhones) {
                 localAssignments[col] = 'phone';
                 return;
              }
              const staticMatch = resolveStaticVocabularies(samples, evaluationTypes);
              if (staticMatch) {
                localAssignments[col] = staticMatch;
                return;
              }
              const dateMatch = evaluateDateType(samples);
              if (dateMatch) {
                localAssignments[col] = dateMatch;
                return;
              }
              samplesPayload[col] = samples;
            });
            Object.entries(localAssignments).forEach(([col, fieldKey]) => {
                addCandidate(fieldKey, col, 'Fase 2 (Datos Locales)');
            });
            if (Object.keys(samplesPayload).length > 0 && companyId) {
               try {
                 const response = await heuristicMatch({ company_id: companyId, columns: samplesPayload });
                 for (const [col, result] of Object.entries(response)) {
                   if (result.bestMatch) {
                     const isIdentity = ['names', 'lastnames'].includes(result.bestMatch);
                     if (isIdentity) {
                       potentialIdentities.push(col);
                     } else {
                        addCandidate(result.bestMatch, col, 'Fase 3 (API DB)');
                     }
                   }
                 }
               } catch (err) {
                 console.error('Heuristic Engine backend failed', err);
               }
            }
          }
          Object.entries(candidateBins).forEach(([fKey, cols]) => {
                     if (['protocol', 'names', 'lastnames'].includes(fKey)) return;
                     if (cols.length === 1) {
                        newMapping[fKey] = cols[0];
                     } else if (cols.length > 1) {
                        const variances = cols.map(col => {
                           const allValues = rows.map(r => getCleanValue(r[col]));
                           return { col, variance: calculateCardinality(allValues) };
                        });
                        variances.sort((a, b) => b.variance - a.variance);
                        newMapping[fKey] = variances[0].col; 
                        if (fKey === 'email' && (!candidateBins['applicant'] || candidateBins['applicant'].length === 0)) {
                           newMapping['applicant'] = variances[variances.length - 1].col;
                        }
                     }
                 });
                 if (candidateBins['protocol'] && candidateBins['protocol'].length > 0) {
                     newMapping['protocol'] = candidateBins['protocol'];
                 }
                 const identityPool = new Set([...potentialIdentities]);
                 (candidateBins['names'] || []).forEach(c => identityPool.add(c));
                 (candidateBins['lastnames'] || []).forEach(c => identityPool.add(c));
                 const pool = Array.from(identityPool).map(col => {
                    const allValues = rows.map(r => getCleanValue(r[col]));
                    return { col, variance: calculateCardinality(allValues), isLastname: col.toLowerCase().includes('apellido') };
                 });
                 pool.sort((a, b) => b.variance - a.variance);
                 if (pool.length > 0) {
                     const patientCols = [...pool];
                     if (patientCols.length === 1) {
                         newMapping['names'] = patientCols[0].col;
                         newMapping['lastnames'] = patientCols[0].col;
                     } else if (patientCols.length >= 2) {
                         if (patientCols[0].isLastname) {
                            newMapping['lastnames'] = patientCols[0].col;
                            newMapping['names'] = patientCols[1].col;
                         } else if (patientCols[1].isLastname) {
                            newMapping['names'] = patientCols[0].col;
                            newMapping['lastnames'] = patientCols[1].col;
                         } else {
                            newMapping['names'] = patientCols[0].col;
                            newMapping['lastnames'] = patientCols[1].col;
                         }
                     }
                 }
          setMapping(newMapping);
          message.success(t('reservations.massiveCharge.step2SuccessFile'));
        }
      } catch (error) {
        console.error('Error parsing excel:', error);
        message.error(t('reservations.massiveCharge.step2ErrorProcessFile'));
      } finally {
        setLoading(false);
      }
    };
    reader.readAsArrayBuffer(file);
    return false;
  };
  const handleMappingChange = (fieldKey, value) => {
    setMapping(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };
  const getAvailableHeaders = (currentFieldKey) => {
    const usedHeaders = Object.entries(mapping)
      .filter(([key]) => key !== currentFieldKey)
      .flatMap(([, val]) => Array.isArray(val) ? val : [val])
      .filter(Boolean);
    return headers.filter(h => !usedHeaders.includes(h));
  };
  const isMandatoryFilled = () => {
    return fieldsToInclude
      .filter(f => f.mandatory)
      .every(f => mapping[f.key] && (Array.isArray(mapping[f.key]) ? mapping[f.key].length > 0 : true));
  };
  const handleNext = () => {
    if (!isMandatoryFilled()) {
      message.warning(t('reservations.massiveCharge.step2WarningMandatory'));
      return;
    }
    if (onMappingComplete) {
      onMappingComplete({ mapping, fileData, headers });
    }
    setCurrentStep(2);
  };
  return (
    <div className="massive-charge-step2">
      <Row justify="center">
        <Col span={24}>
          <h3 className="massive-charge-step-title">{t('reservations.massiveCharge.step2Title')}</h3>
          <p className="massive-charge-step-description">
            {t('reservations.massiveCharge.step2Desc')}
          </p>
        </Col>
      </Row>
      {loading ? (
        <Card className="text-center massive-charge-upload-card">
          <Spin size="large" />
          <p className="massive-charge-upload-desc">
            {t('reservations.massiveCharge.step2Analyzing')}
          </p>
        </Card>
      ) : (
        <>
          <Dragger
            accept=".xlsx"
            beforeUpload={handleBeforeUpload}
            showUploadList={false}
            className="mb-30"
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">{t('reservations.massiveCharge.step2ClickOrDrag')}</p>
            <p className="ant-upload-hint">{t('reservations.massiveCharge.step2OnlyExcelAllowed')}</p>
          </Dragger>
          {headers.length > 0 && (
            <Card title={t('reservations.massiveCharge.step2MappingTitle')} className="massive-charge-mapping-card">
              <Row gutter={[20, 20]}>
                {fieldsToInclude.map(field => (
                  <Col lg={12} md={12} sm={24} key={field.key}>
                    <div className="mapping-item">
                      <span className={`mapping-label ${field.mandatory ? 'mandatory' : ''}`}>
                        {t(`reservations.massiveCharge.fields.${field.key}`)}
                      </span>
                      <Select
                        className="cmt-select w-100"
                        placeholder={t('reservations.massiveCharge.step2SelectColumnPlaceholder')}
                        value={mapping[field.key]}
                        onChange={(val) => handleMappingChange(field.key, val)}
                        allowClear
                        mode={field.key === 'protocol' ? 'multiple' : undefined}
                      >
                        {getAvailableHeaders(field.key).map(header => (
                          <Option key={header} value={header}>{header}</Option>
                        ))}
                      </Select>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card>
          )}
        </>
      )}
      <Row justify="space-between" className="mt-30">
        <Col>
          <Button
            className="cmt-button primary outline"
            size="large"
            icon={<ArrowLeftOutlined />}
            onClick={() => setCurrentStep(0)}
          >
            {t('reservations.massiveCharge.step2Back')}
          </Button>
        </Col>
        <Col>
          <Button
            type="primary"
            className="cmt-button primary"
            size="large"
            onClick={handleNext}
            disabled={!fileData || !isMandatoryFilled()}
            loading={loading}
          >
            {t('reservations.massiveCharge.step1Next')}
            <ArrowRightOutlined />
          </Button>
        </Col>
      </Row>
    </div>
  );
};
export default MassiveChargeStep2;
